import type { CollectionSchema } from '@shared/types';

/** Must match backend SCHEMA_CACHE_TTL_MS so cached schema is accepted. */
const SCHEMA_CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes

const STORAGE_KEY_PREFIX = 'firestore-schema:';

function cacheKey(connectionId: string, collectionPath: string): string {
  return `${connectionId}\n${collectionPath}`;
}

const memory = new Map<string, { schema: CollectionSchema; cachedAt: number }>();

function readFromStorage(key: string): { schema: CollectionSchema; cachedAt: number } | null {
  if (typeof sessionStorage === 'undefined') return null;
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY_PREFIX + key);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { schema: CollectionSchema; cachedAt: number };
    if (
      !parsed.schema ||
      typeof parsed.schema !== 'object' ||
      typeof parsed.cachedAt !== 'number'
    ) {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

function writeToStorage(key: string, schema: CollectionSchema, cachedAt: number): void {
  if (typeof sessionStorage === 'undefined') return;
  try {
    sessionStorage.setItem(
      STORAGE_KEY_PREFIX + key,
      JSON.stringify({ schema, cachedAt })
    );
  } catch {
    // ignore storage errors
  }
}

function isExpired(cachedAt: number): boolean {
  const now = Date.now();
  return cachedAt > now || now - cachedAt > SCHEMA_CACHE_TTL_MS;
}

export function getSchema(
  connectionId: string,
  collectionPath: string
): { schema: CollectionSchema; cachedAt: number } | null {
  const key = cacheKey(connectionId, collectionPath);
  let entry = memory.get(key);
  if (!entry) {
    entry = readFromStorage(key);
    if (entry) memory.set(key, entry);
  }
  if (!entry || isExpired(entry.cachedAt)) return null;
  return entry;
}

export function setSchema(
  connectionId: string,
  collectionPath: string,
  schema: CollectionSchema
): void {
  const cachedAt = Date.now();
  const key = cacheKey(connectionId, collectionPath);
  const entry = { schema, cachedAt };
  memory.set(key, entry);
  writeToStorage(key, schema, cachedAt);
}
